"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports["default"] = void 0;

var _Instruction = _interopRequireDefault(require("./Instruction.js"));

var _TextBuilder = require("./TextBuilder.js");

var _has = require("../../has.js");

var _transform = require("../../transform.js");

var _extent = require("../../extent.js");

var _canvas = require("../canvas.js");

var _textpath = require("../../geom/flat/textpath.js");

var _array = require("../../array.js");

var _length = require("../../geom/flat/length.js");

var _transform2 = require("../../geom/flat/transform.js");

function _interopRequireDefault(obj) { return obj && obj.__esModule ? obj : { "default": obj }; }

/**
 * @module ol/render/canvas/Executor
 */

/**
 * @typedef {Object} BBox
 * @property {number} minX Minimal x.
 * @property {number} minY Minimal y.
 * @property {number} maxX Maximal x.
 * @property {number} maxY Maximal y
 * @property {*} value Value.
 */

/**
 * @typedef {Object} ImageOrLabelDimensions
 * @property {number} drawImageX DrawImageX.
 * @property {number} drawImageY DrawImageY.
 * @property {number} drawImageW DrawImageW.
 * @property {number} drawImageH DrawImageH.
 * @property {number} originX OriginX.
 * @property {number} originY OriginY.
 * @property {Array<number>} scale Scale.
 * @property {BBox} declutterBox DeclutterBox.
 * @property {import("../../transform.js").Transform} canvasTransform CanvasTransform.
 */

/**
 * @typedef {{0: CanvasRenderingContext2D, 1: number, 2: import("../canvas.js").Label|HTMLImageElement|HTMLCanvasElement|HTMLVideoElement, 3: ImageOrLabelDimensions, 4: number, 5: Array<*>, 6: Array<*>}} ReplayImageOrLabelArgs
 */

/**
 * @template T
 * @typedef {function(import("../../Feature.js").FeatureLike, import("../../geom/SimpleGeometry.js").default): T} FeatureCallback
 */

/**
 * @type {import("../../extent.js").Extent}
 */
var tmpExtent = (0, _extent.createEmpty)();
/** @type {import("../../coordinate.js").Coordinate} */

var p1 = [];
/** @type {import("../../coordinate.js").Coordinate} */

var p2 = [];
/** @type {import("../../coordinate.js").Coordinate} */

var p3 = [];
/** @type {import("../../coordinate.js").Coordinate} */

var p4 = [];
/**
 * @param {ReplayImageOrLabelArgs} replayImageOrLabelArgs Arguments to replayImageOrLabel
 * @return {BBox} Declutter bbox.
 */

function getDeclutterBox(replayImageOrLabelArgs) {
  return replayImageOrLabelArgs[3].declutterBox;
}

var rtlRegEx = new RegExp(
/* eslint-disable prettier/prettier */
'[' + String.fromCharCode(0x00591) + '-' + String.fromCharCode(0x008ff) + String.fromCharCode(0x0fb1d) + '-' + String.fromCharCode(0x0fdff) + String.fromCharCode(0x0fe70) + '-' + String.fromCharCode(0x0fefc) + String.fromCharCode(0x10800) + '-' + String.fromCharCode(0x10fff) + String.fromCharCode(0x1e800) + '-' + String.fromCharCode(0x1efff) + ']'
/* eslint-enable prettier/prettier */
);
/**
 * @param {string} text Text.
 * @param {string} align Alignment.
 * @return {number} Text alignment.
 */

function horizontalTextAlign(text, align) {
  if ((align === 'start' || align === 'end') && !rtlRegEx.test(text)) {
    align = align === 'start' ? 'left' : 'right';
  }

  return _TextBuilder.TEXT_ALIGN[align];
}
/**
 * @param {Array<string>} acc Accumulator.
 * @param {string} line Line of text.
 * @param {number} i Index
 * @return {Array<string>} Accumulator.
 */


function createTextChunks(acc, line, i) {
  if (i > 0) {
    acc.push('\n', '');
  }

  acc.push(line, '');
  return acc;
}

var Executor =
/** @class */
function () {
  /**
   * @param {number} resolution Resolution.
   * @param {number} pixelRatio Pixel ratio.
   * @param {boolean} overlaps The replay can have overlapping geometries.
   * @param {import("../canvas.js").SerializableInstructions} instructions The serializable instructions
   */
  function Executor(resolution, pixelRatio, overlaps, instructions) {
    /**
     * @protected
     * @type {boolean}
     */
    this.overlaps = overlaps;
    /**
     * @protected
     * @type {number}
     */

    this.pixelRatio = pixelRatio;
    /**
     * @protected
     * @const
     * @type {number}
     */

    this.resolution = resolution;
    /**
     * @private
     * @type {boolean}
     */

    this.alignFill_;
    /**
     * @protected
     * @type {Array<*>}
     */

    this.instructions = instructions.instructions;
    /**
     * @protected
     * @type {Array<number>}
     */

    this.coordinates = instructions.coordinates;
    /**
     * @private
     * @type {!Object<number,import("../../coordinate.js").Coordinate|Array<import("../../coordinate.js").Coordinate>|Array<Array<import("../../coordinate.js").Coordinate>>>}
     */

    this.coordinateCache_ = {};
    /**
     * @private
     * @type {!import("../../transform.js").Transform}
     */

    this.renderedTransform_ = (0, _transform.create)();
    /**
     * @protected
     * @type {Array<*>}
     */

    this.hitDetectionInstructions = instructions.hitDetectionInstructions;
    /**
     * @private
     * @type {Array<number>}
     */

    this.pixelCoordinates_ = null;
    /**
     * @private
     * @type {number}
     */

    this.viewRotation_ = 0;
    /**
     * @type {!Object<string, import("../canvas.js").FillState>}
     */

    this.fillStates = instructions.fillStates || {};
    /**
     * @type {!Object<string, import("../canvas.js").StrokeState>}
     */

    this.strokeStates = instructions.strokeStates || {};
    /**
     * @type {!Object<string, import("../canvas.js").TextState>}
     */

    this.textStates = instructions.textStates || {};
    /**
     * @private
     * @type {Object<string, Object<string, number>>}
     */

    this.widths_ = {};
    /**
     * @private
     * @type {Object<string, import("../canvas.js").Label>}
     */

    this.labels_ = {};
  }
  /**
   * @param {string|Array<string>} text Text.
   * @param {string} textKey Text style key.
   * @param {string} fillKey Fill style key.
   * @param {string} strokeKey Stroke style key.
   * @return {import("../canvas.js").Label} Label.
   */


  Executor.prototype.createLabel = function (text, textKey, fillKey, strokeKey) {
    var key = text + textKey + fillKey + strokeKey;

    if (this.labels_[key]) {
      return this.labels_[key];
    }

    var strokeState = strokeKey ? this.strokeStates[strokeKey] : null;
    var fillState = fillKey ? this.fillStates[fillKey] : null;
    var textState = this.textStates[textKey];
    var pixelRatio = this.pixelRatio;
    var scale = [textState.scale[0] * pixelRatio, textState.scale[1] * pixelRatio];
    var textIsArray = Array.isArray(text);
    var align = horizontalTextAlign(textIsArray ? text[0] : text, textState.textAlign || _canvas.defaultTextAlign);
    var strokeWidth = strokeKey && strokeState.lineWidth ? strokeState.lineWidth : 0;
    var chunks = textIsArray ? text : text.split('\n').reduce(createTextChunks, []);

    var _a = (0, _canvas.getTextDimensions)(textState, chunks),
        width = _a.width,
        height = _a.height,
        widths = _a.widths,
        heights = _a.heights,
        lineWidths = _a.lineWidths;

    var renderWidth = width + strokeWidth;
    var contextInstructions = []; // make canvas 2 pixels wider to account for italic text width measurement errors

    var w = (renderWidth + 2) * scale[0];
    var h = (height + strokeWidth) * scale[1];
    /** @type {import("../canvas.js").Label} */

    var label = {
      width: w < 0 ? Math.floor(w) : Math.ceil(w),
      height: h < 0 ? Math.floor(h) : Math.ceil(h),
      contextInstructions: contextInstructions
    };

    if (scale[0] != 1 || scale[1] != 1) {
      contextInstructions.push('scale', scale);
    }

    if (strokeKey) {
      contextInstructions.push('strokeStyle', strokeState.strokeStyle);
      contextInstructions.push('lineWidth', strokeWidth);
      contextInstructions.push('lineCap', strokeState.lineCap);
      contextInstructions.push('lineJoin', strokeState.lineJoin);
      contextInstructions.push('miterLimit', strokeState.miterLimit); // eslint-disable-next-line

      var Context = _has.WORKER_OFFSCREEN_CANVAS ? OffscreenCanvasRenderingContext2D : CanvasRenderingContext2D;

      if (Context.prototype.setLineDash) {
        contextInstructions.push('setLineDash', [strokeState.lineDash]);
        contextInstructions.push('lineDashOffset', strokeState.lineDashOffset);
      }
    }

    if (fillKey) {
      contextInstructions.push('fillStyle', fillState.fillStyle);
    }

    contextInstructions.push('textBaseline', 'middle');
    contextInstructions.push('textAlign', 'center');
    var leftRight = 0.5 - align;
    var x = align * renderWidth + leftRight * strokeWidth;
    var strokeInstructions = [];
    var fillInstructions = [];
    var lineHeight = 0;
    var lineOffset = 0;
    var widthHeightIndex = 0;
    var lineWidthIndex = 0;
    var previousFont;

    for (var i = 0, ii = chunks.length; i < ii; i += 2) {
      var text_1 = chunks[i];

      if (text_1 === '\n') {
        lineOffset += lineHeight;
        lineHeight = 0;
        x = align * renderWidth + leftRight * strokeWidth;
        ++lineWidthIndex;
        continue;
      }

      var font = chunks[i + 1] || textState.font;

      if (font !== previousFont) {
        if (strokeKey) {
          strokeInstructions.push('font', font);
        }

        if (fillKey) {
          fillInstructions.push('font', font);
        }

        previousFont = font;
      }

      lineHeight = Math.max(lineHeight, heights[widthHeightIndex]);
      var fillStrokeArgs = [text_1, x + leftRight * widths[widthHeightIndex] + align * (widths[widthHeightIndex] - lineWidths[lineWidthIndex]), 0.5 * (strokeWidth + lineHeight) + lineOffset];
      x += widths[widthHeightIndex];

      if (strokeKey) {
        strokeInstructions.push('strokeText', fillStrokeArgs);
      }

      if (fillKey) {
        fillInstructions.push('fillText', fillStrokeArgs);
      }

      ++widthHeightIndex;
    }

    Array.prototype.push.apply(contextInstructions, strokeInstructions);
    Array.prototype.push.apply(contextInstructions, fillInstructions);
    this.labels_[key] = label;
    return label;
  };
  /**
   * @param {CanvasRenderingContext2D} context Context.
   * @param {import("../../coordinate.js").Coordinate} p1 1st point of the background box.
   * @param {import("../../coordinate.js").Coordinate} p2 2nd point of the background box.
   * @param {import("../../coordinate.js").Coordinate} p3 3rd point of the background box.
   * @param {import("../../coordinate.js").Coordinate} p4 4th point of the background box.
   * @param {Array<*>} fillInstruction Fill instruction.
   * @param {Array<*>} strokeInstruction Stroke instruction.
   */


  Executor.prototype.replayTextBackground_ = function (context, p1, p2, p3, p4, fillInstruction, strokeInstruction) {
    context.beginPath();
    context.moveTo.apply(context, p1);
    context.lineTo.apply(context, p2);
    context.lineTo.apply(context, p3);
    context.lineTo.apply(context, p4);
    context.lineTo.apply(context, p1);

    if (fillInstruction) {
      this.alignFill_ =
      /** @type {boolean} */
      fillInstruction[2];
      this.fill_(context);
    }

    if (strokeInstruction) {
      this.setStrokeStyle_(context,
      /** @type {Array<*>} */
      strokeInstruction);
      context.stroke();
    }
  };
  /**
   * @private
   * @param {number} sheetWidth Width of the sprite sheet.
   * @param {number} sheetHeight Height of the sprite sheet.
   * @param {number} centerX X.
   * @param {number} centerY Y.
   * @param {number} width Width.
   * @param {number} height Height.
   * @param {number} anchorX Anchor X.
   * @param {number} anchorY Anchor Y.
   * @param {number} originX Origin X.
   * @param {number} originY Origin Y.
   * @param {number} rotation Rotation.
   * @param {import("../../size.js").Size} scale Scale.
   * @param {boolean} snapToPixel Snap to pixel.
   * @param {Array<number>} padding Padding.
   * @param {boolean} fillStroke Background fill or stroke.
   * @param {import("../../Feature.js").FeatureLike} feature Feature.
   * @return {ImageOrLabelDimensions} Dimensions for positioning and decluttering the image or label.
   */


  Executor.prototype.calculateImageOrLabelDimensions_ = function (sheetWidth, sheetHeight, centerX, centerY, width, height, anchorX, anchorY, originX, originY, rotation, scale, snapToPixel, padding, fillStroke, feature) {
    anchorX *= scale[0];
    anchorY *= scale[1];
    var x = centerX - anchorX;
    var y = centerY - anchorY;
    var w = width + originX > sheetWidth ? sheetWidth - originX : width;
    var h = height + originY > sheetHeight ? sheetHeight - originY : height;
    var boxW = padding[3] + w * scale[0] + padding[1];
    var boxH = padding[0] + h * scale[1] + padding[2];
    var boxX = x - padding[3];
    var boxY = y - padding[0];

    if (fillStroke || rotation !== 0) {
      p1[0] = boxX;
      p4[0] = boxX;
      p1[1] = boxY;
      p2[1] = boxY;
      p2[0] = boxX + boxW;
      p3[0] = p2[0];
      p3[1] = boxY + boxH;
      p4[1] = p3[1];
    }

    var transform;

    if (rotation !== 0) {
      transform = (0, _transform.compose)((0, _transform.create)(), centerX, centerY, 1, 1, rotation, -centerX, -centerY);
      (0, _transform.apply)(transform, p1);
      (0, _transform.apply)(transform, p2);
      (0, _transform.apply)(transform, p3);
      (0, _transform.apply)(transform, p4);
      (0, _extent.createOrUpdate)(Math.min(p1[0], p2[0], p3[0], p4[0]), Math.min(p1[1], p2[1], p3[1], p4[1]), Math.max(p1[0], p2[0], p3[0], p4[0]), Math.max(p1[1], p2[1], p3[1], p4[1]), tmpExtent);
    } else {
      (0, _extent.createOrUpdate)(Math.min(boxX, boxX + boxW), Math.min(boxY, boxY + boxH), Math.max(boxX, boxX + boxW), Math.max(boxY, boxY + boxH), tmpExtent);
    }

    if (snapToPixel) {
      x = Math.round(x);
      y = Math.round(y);
    }

    return {
      drawImageX: x,
      drawImageY: y,
      drawImageW: w,
      drawImageH: h,
      originX: originX,
      originY: originY,
      declutterBox: {
        minX: tmpExtent[0],
        minY: tmpExtent[1],
        maxX: tmpExtent[2],
        maxY: tmpExtent[3],
        value: feature
      },
      canvasTransform: transform,
      scale: scale
    };
  };
  /**
   * @private
   * @param {CanvasRenderingContext2D} context Context.
   * @param {number} contextScale Scale of the context.
   * @param {import("../canvas.js").Label|HTMLImageElement|HTMLCanvasElement|HTMLVideoElement} imageOrLabel Image.
   * @param {ImageOrLabelDimensions} dimensions Dimensions.
   * @param {number} opacity Opacity.
   * @param {Array<*>} fillInstruction Fill instruction.
   * @param {Array<*>} strokeInstruction Stroke instruction.
   * @return {boolean} The image or label was rendered.
   */


  Executor.prototype.replayImageOrLabel_ = function (context, contextScale, imageOrLabel, dimensions, opacity, fillInstruction, strokeInstruction) {
    var fillStroke = !!(fillInstruction || strokeInstruction);
    var box = dimensions.declutterBox;
    var canvas = context.canvas;
    var strokePadding = strokeInstruction ? strokeInstruction[2] * dimensions.scale[0] / 2 : 0;
    var intersects = box.minX - strokePadding <= canvas.width / contextScale && box.maxX + strokePadding >= 0 && box.minY - strokePadding <= canvas.height / contextScale && box.maxY + strokePadding >= 0;

    if (intersects) {
      if (fillStroke) {
        this.replayTextBackground_(context, p1, p2, p3, p4,
        /** @type {Array<*>} */
        fillInstruction,
        /** @type {Array<*>} */
        strokeInstruction);
      }

      (0, _canvas.drawImageOrLabel)(context, dimensions.canvasTransform, opacity, imageOrLabel, dimensions.originX, dimensions.originY, dimensions.drawImageW, dimensions.drawImageH, dimensions.drawImageX, dimensions.drawImageY, dimensions.scale);
    }

    return true;
  };
  /**
   * @private
   * @param {CanvasRenderingContext2D} context Context.
   */


  Executor.prototype.fill_ = function (context) {
    if (this.alignFill_) {
      var origin_1 = (0, _transform.apply)(this.renderedTransform_, [0, 0]);
      var repeatSize = 512 * this.pixelRatio;
      context.save();
      context.translate(origin_1[0] % repeatSize, origin_1[1] % repeatSize);
      context.rotate(this.viewRotation_);
    }

    context.fill();

    if (this.alignFill_) {
      context.restore();
    }
  };
  /**
   * @private
   * @param {CanvasRenderingContext2D} context Context.
   * @param {Array<*>} instruction Instruction.
   */


  Executor.prototype.setStrokeStyle_ = function (context, instruction) {
    context['strokeStyle'] =
    /** @type {import("../../colorlike.js").ColorLike} */
    instruction[1];
    context.lineWidth =
    /** @type {number} */
    instruction[2];
    context.lineCap =
    /** @type {CanvasLineCap} */
    instruction[3];
    context.lineJoin =
    /** @type {CanvasLineJoin} */
    instruction[4];
    context.miterLimit =
    /** @type {number} */
    instruction[5];

    if (context.setLineDash) {
      context.lineDashOffset =
      /** @type {number} */
      instruction[7];
      context.setLineDash(
      /** @type {Array<number>} */
      instruction[6]);
    }
  };
  /**
   * @private
   * @param {string|Array<string>} text The text to draw.
   * @param {string} textKey The key of the text state.
   * @param {string} strokeKey The key for the stroke state.
   * @param {string} fillKey The key for the fill state.
   * @return {{label: import("../canvas.js").Label, anchorX: number, anchorY: number}} The text image and its anchor.
   */


  Executor.prototype.drawLabelWithPointPlacement_ = function (text, textKey, strokeKey, fillKey) {
    var textState = this.textStates[textKey];
    var label = this.createLabel(text, textKey, fillKey, strokeKey);
    var strokeState = this.strokeStates[strokeKey];
    var pixelRatio = this.pixelRatio;
    var align = horizontalTextAlign(Array.isArray(text) ? text[0] : text, textState.textAlign || _canvas.defaultTextAlign);
    var baseline = _TextBuilder.TEXT_ALIGN[textState.textBaseline || _canvas.defaultTextBaseline];
    var strokeWidth = strokeState && strokeState.lineWidth ? strokeState.lineWidth : 0; // Remove the 2 pixels we added in createLabel() for the anchor

    var width = label.width / pixelRatio - 2 * textState.scale[0];
    var anchorX = align * width + 2 * (0.5 - align) * strokeWidth;
    var anchorY = baseline * label.height / pixelRatio + 2 * (0.5 - baseline) * strokeWidth;
    return {
      label: label,
      anchorX: anchorX,
      anchorY: anchorY
    };
  };
  /**
   * @private
   * @param {CanvasRenderingContext2D} context Context.
   * @param {number} contextScale Scale of the context.
   * @param {import("../../transform.js").Transform} transform Transform.
   * @param {Array<*>} instructions Instructions array.
   * @param {boolean} snapToPixel Snap point symbols and text to integer pixels.
   * @param {FeatureCallback<T>} [opt_featureCallback] Feature callback.
   * @param {import("../../extent.js").Extent} [opt_hitExtent] Only check
   *     features that intersect this extent.
   * @param {import("rbush").default} [opt_declutterTree] Declutter tree.
   * @return {T|undefined} Callback result.
   * @template T
   */


  Executor.prototype.execute_ = function (context, contextScale, transform, instructions, snapToPixel, opt_featureCallback, opt_hitExtent, opt_declutterTree) {
    /** @type {Array<number>} */
    var pixelCoordinates;

    if (this.pixelCoordinates_ && (0, _array.equals)(transform, this.renderedTransform_)) {
      pixelCoordinates = this.pixelCoordinates_;
    } else {
      if (!this.pixelCoordinates_) {
        this.pixelCoordinates_ = [];
      }

      pixelCoordinates = (0, _transform2.transform2D)(this.coordinates, 0, this.coordinates.length, 2, transform, this.pixelCoordinates_);
      (0, _transform.setFromArray)(this.renderedTransform_, transform);
    }

    var i = 0; // instruction index

    var ii = instructions.length; // end of instructions

    var d = 0; // data index

    var dd; // end of per-instruction data

    var anchorX, anchorY, prevX, prevY, roundX, roundY, image, text, textKey, strokeKey, fillKey;
    var pendingFill = 0;
    var pendingStroke = 0;
    var lastFillInstruction = null;
    var lastStrokeInstruction = null;
    var coordinateCache = this.coordinateCache_;
    var viewRotation = this.viewRotation_;
    var viewRotationFromTransform = Math.round(Math.atan2(-transform[1], transform[0]) * 1e12) / 1e12;
    var state =
    /** @type {import("../../render.js").State} */
    {
      context: context,
      pixelRatio: this.pixelRatio,
      resolution: this.resolution,
      rotation: viewRotation
    }; // When the batch size gets too big, performance decreases. 200 is a good
    // balance between batch size and number of fill/stroke instructions.

    var batchSize = this.instructions != instructions || this.overlaps ? 0 : 200;
    var
    /** @type {import("../../Feature.js").FeatureLike} */
    feature;
    var x, y, currentGeometry;

    while (i < ii) {
      var instruction = instructions[i];
      var type =
      /** @type {import("./Instruction.js").default} */
      instruction[0];

      switch (type) {
        case _Instruction["default"].BEGIN_GEOMETRY:
          feature =
          /** @type {import("../../Feature.js").FeatureLike} */
          instruction[1];
          currentGeometry = instruction[3];

          if (!feature.getGeometry()) {
            i =
            /** @type {number} */
            instruction[2];
          } else if (opt_hitExtent !== undefined && !(0, _extent.intersects)(opt_hitExtent, currentGeometry.getExtent())) {
            i =
            /** @type {number} */
            instruction[2] + 1;
          } else {
            ++i;
          }

          break;

        case _Instruction["default"].BEGIN_PATH:
          if (pendingFill > batchSize) {
            this.fill_(context);
            pendingFill = 0;
          }

          if (pendingStroke > batchSize) {
            context.stroke();
            pendingStroke = 0;
          }

          if (!pendingFill && !pendingStroke) {
            context.beginPath();
            prevX = NaN;
            prevY = NaN;
          }

          ++i;
          break;

        case _Instruction["default"].CIRCLE:
          d =
          /** @type {number} */
          instruction[1];
          var x1 = pixelCoordinates[d];
          var y1 = pixelCoordinates[d + 1];
          var x2 = pixelCoordinates[d + 2];
          var y2 = pixelCoordinates[d + 3];
          var dx = x2 - x1;
          var dy = y2 - y1;
          var r = Math.sqrt(dx * dx + dy * dy);
          context.moveTo(x1 + r, y1);
          context.arc(x1, y1, r, 0, 2 * Math.PI, true);
          ++i;
          break;

        case _Instruction["default"].CLOSE_PATH:
          context.closePath();
          ++i;
          break;

        case _Instruction["default"].CUSTOM:
          d =
          /** @type {number} */
          instruction[1];
          dd = instruction[2];
          var geometry =
          /** @type {import("../../geom/SimpleGeometry.js").default} */
          instruction[3];
          var renderer = instruction[4];
          var fn = instruction.length == 6 ? instruction[5] : undefined;
          state.geometry = geometry;
          state.feature = feature;

          if (!(i in coordinateCache)) {
            coordinateCache[i] = [];
          }

          var coords = coordinateCache[i];

          if (fn) {
            fn(pixelCoordinates, d, dd, 2, coords);
          } else {
            coords[0] = pixelCoordinates[d];
            coords[1] = pixelCoordinates[d + 1];
            coords.length = 2;
          }

          renderer(coords, state);
          ++i;
          break;

        case _Instruction["default"].DRAW_IMAGE:
          d =
          /** @type {number} */
          instruction[1];
          dd =
          /** @type {number} */
          instruction[2];
          image =
          /** @type {HTMLCanvasElement|HTMLVideoElement|HTMLImageElement} */
          instruction[3]; // Remaining arguments in DRAW_IMAGE are in alphabetical order

          anchorX =
          /** @type {number} */
          instruction[4];
          anchorY =
          /** @type {number} */
          instruction[5];
          var height =
          /** @type {number} */
          instruction[6];
          var opacity =
          /** @type {number} */
          instruction[7];
          var originX =
          /** @type {number} */
          instruction[8];
          var originY =
          /** @type {number} */
          instruction[9];
          var rotateWithView =
          /** @type {boolean} */
          instruction[10];
          var rotation =
          /** @type {number} */
          instruction[11];
          var scale =
          /** @type {import("../../size.js").Size} */
          instruction[12];
          var width =
          /** @type {number} */
          instruction[13];
          var declutterImageWithText =
          /** @type {import("../canvas.js").DeclutterImageWithText} */
          instruction[14];

          if (!image && instruction.length >= 19) {
            // create label images
            text =
            /** @type {string} */
            instruction[18];
            textKey =
            /** @type {string} */
            instruction[19];
            strokeKey =
            /** @type {string} */
            instruction[20];
            fillKey =
            /** @type {string} */
            instruction[21];
            var labelWithAnchor = this.drawLabelWithPointPlacement_(text, textKey, strokeKey, fillKey);
            image = labelWithAnchor.label;
            instruction[3] = image;
            var textOffsetX =
            /** @type {number} */
            instruction[22];
            anchorX = (labelWithAnchor.anchorX - textOffsetX) * this.pixelRatio;
            instruction[4] = anchorX;
            var textOffsetY =
            /** @type {number} */
            instruction[23];
            anchorY = (labelWithAnchor.anchorY - textOffsetY) * this.pixelRatio;
            instruction[5] = anchorY;
            height = image.height;
            instruction[6] = height;
            width = image.width;
            instruction[13] = width;
          }

          var geometryWidths = void 0;

          if (instruction.length > 24) {
            geometryWidths =
            /** @type {number} */
            instruction[24];
          }

          var padding = void 0,
              backgroundFill = void 0,
              backgroundStroke = void 0;

          if (instruction.length > 16) {
            padding =
            /** @type {Array<number>} */
            instruction[15];
            backgroundFill =
            /** @type {boolean} */
            instruction[16];
            backgroundStroke =
            /** @type {boolean} */
            instruction[17];
          } else {
            padding = _canvas.defaultPadding;
            backgroundFill = false;
            backgroundStroke = false;
          }

          if (rotateWithView && viewRotationFromTransform) {
            // Canvas is expected to be rotated to reverse view rotation.
            rotation += viewRotation;
          } else if (!rotateWithView && !viewRotationFromTransform) {
            // Canvas is not rotated, images need to be rotated back to be north-up.
            rotation -= viewRotation;
          }

          var widthIndex = 0;

          for (; d < dd; d += 2) {
            if (geometryWidths && geometryWidths[widthIndex++] < width / this.pixelRatio) {
              continue;
            }

            var dimensions = this.calculateImageOrLabelDimensions_(image.width, image.height, pixelCoordinates[d], pixelCoordinates[d + 1], width, height, anchorX, anchorY, originX, originY, rotation, scale, snapToPixel, padding, backgroundFill || backgroundStroke, feature);
            /** @type {ReplayImageOrLabelArgs} */

            var args = [context, contextScale, image, dimensions, opacity, backgroundFill ?
            /** @type {Array<*>} */
            lastFillInstruction : null, backgroundStroke ?
            /** @type {Array<*>} */
            lastStrokeInstruction : null];
            var imageArgs = void 0;
            var imageDeclutterBox = void 0;

            if (opt_declutterTree && declutterImageWithText) {
              var index = dd - d;

              if (!declutterImageWithText[index]) {
                // We now have the image for an image+text combination.
                declutterImageWithText[index] = args; // Don't render anything for now, wait for the text.

                continue;
              }

              imageArgs = declutterImageWithText[index];
              delete declutterImageWithText[index];
              imageDeclutterBox = getDeclutterBox(imageArgs);

              if (opt_declutterTree.collides(imageDeclutterBox)) {
                continue;
              }
            }

            if (opt_declutterTree && opt_declutterTree.collides(dimensions.declutterBox)) {
              continue;
            }

            if (imageArgs) {
              // We now have image and text for an image+text combination.
              if (opt_declutterTree) {
                opt_declutterTree.insert(imageDeclutterBox);
              } // Render the image before we render the text.


              this.replayImageOrLabel_.apply(this, imageArgs);
            }

            if (opt_declutterTree) {
              opt_declutterTree.insert(dimensions.declutterBox);
            }

            this.replayImageOrLabel_.apply(this, args);
          }

          ++i;
          break;

        case _Instruction["default"].DRAW_CHARS:
          var begin =
          /** @type {number} */
          instruction[1];
          var end =
          /** @type {number} */
          instruction[2];
          var baseline =
          /** @type {number} */
          instruction[3];
          var overflow =
          /** @type {number} */
          instruction[4];
          fillKey =
          /** @type {string} */
          instruction[5];
          var maxAngle =
          /** @type {number} */
          instruction[6];
          var measurePixelRatio =
          /** @type {number} */
          instruction[7];
          var offsetY =
          /** @type {number} */
          instruction[8];
          strokeKey =
          /** @type {string} */
          instruction[9];
          var strokeWidth =
          /** @type {number} */
          instruction[10];
          text =
          /** @type {string} */
          instruction[11];
          textKey =
          /** @type {string} */
          instruction[12];
          var pixelRatioScale = [
          /** @type {number} */
          instruction[13],
          /** @type {number} */
          instruction[13]];
          var textState = this.textStates[textKey];
          var font = textState.font;
          var textScale = [textState.scale[0] * measurePixelRatio, textState.scale[1] * measurePixelRatio];
          var cachedWidths = void 0;

          if (font in this.widths_) {
            cachedWidths = this.widths_[font];
          } else {
            cachedWidths = {};
            this.widths_[font] = cachedWidths;
          }

          var pathLength = (0, _length.lineStringLength)(pixelCoordinates, begin, end, 2);
          var textLength = Math.abs(textScale[0]) * (0, _canvas.measureAndCacheTextWidth)(font, text, cachedWidths);

          if (overflow || textLength <= pathLength) {
            var textAlign = this.textStates[textKey].textAlign;
            var startM = (pathLength - textLength) * _TextBuilder.TEXT_ALIGN[textAlign];
            var parts = (0, _textpath.drawTextOnPath)(pixelCoordinates, begin, end, 2, text, startM, maxAngle, Math.abs(textScale[0]), _canvas.measureAndCacheTextWidth, font, cachedWidths, viewRotationFromTransform ? 0 : this.viewRotation_);

            drawChars: if (parts) {
              /** @type {Array<ReplayImageOrLabelArgs>} */
              var replayImageOrLabelArgs = [];
              var c = void 0,
                  cc = void 0,
                  chars = void 0,
                  label = void 0,
                  part = void 0;

              if (strokeKey) {
                for (c = 0, cc = parts.length; c < cc; ++c) {
                  part = parts[c]; // x, y, anchorX, rotation, chunk

                  chars =
                  /** @type {string} */
                  part[4];
                  label = this.createLabel(chars, textKey, '', strokeKey);
                  anchorX =
                  /** @type {number} */
                  part[2] + (textScale[0] < 0 ? -strokeWidth : strokeWidth);
                  anchorY = baseline * label.height + (0.5 - baseline) * 2 * strokeWidth * textScale[1] / textScale[0] - offsetY;
                  var dimensions = this.calculateImageOrLabelDimensions_(label.width, label.height, part[0], part[1], label.width, label.height, anchorX, anchorY, 0, 0, part[3], pixelRatioScale, false, _canvas.defaultPadding, false, feature);

                  if (opt_declutterTree && opt_declutterTree.collides(dimensions.declutterBox)) {
                    break drawChars;
                  }

                  replayImageOrLabelArgs.push([context, contextScale, label, dimensions, 1, null, null]);
                }
              }

              if (fillKey) {
                for (c = 0, cc = parts.length; c < cc; ++c) {
                  part = parts[c]; // x, y, anchorX, rotation, chunk

                  chars =
                  /** @type {string} */
                  part[4];
                  label = this.createLabel(chars, textKey, fillKey, '');
                  anchorX =
                  /** @type {number} */
                  part[2];
                  anchorY = baseline * label.height - offsetY;
                  var dimensions = this.calculateImageOrLabelDimensions_(label.width, label.height, part[0], part[1], label.width, label.height, anchorX, anchorY, 0, 0, part[3], pixelRatioScale, false, _canvas.defaultPadding, false, feature);

                  if (opt_declutterTree && opt_declutterTree.collides(dimensions.declutterBox)) {
                    break drawChars;
                  }

                  replayImageOrLabelArgs.push([context, contextScale, label, dimensions, 1, null, null]);
                }
              }

              if (opt_declutterTree) {
                opt_declutterTree.load(replayImageOrLabelArgs.map(getDeclutterBox));
              }

              for (var i_1 = 0, ii_1 = replayImageOrLabelArgs.length; i_1 < ii_1; ++i_1) {
                this.replayImageOrLabel_.apply(this, replayImageOrLabelArgs[i_1]);
              }
            }
          }

          ++i;
          break;

        case _Instruction["default"].END_GEOMETRY:
          if (opt_featureCallback !== undefined) {
            feature =
            /** @type {import("../../Feature.js").FeatureLike} */
            instruction[1];
            var result = opt_featureCallback(feature, currentGeometry);

            if (result) {
              return result;
            }
          }

          ++i;
          break;

        case _Instruction["default"].FILL:
          if (batchSize) {
            pendingFill++;
          } else {
            this.fill_(context);
          }

          ++i;
          break;

        case _Instruction["default"].MOVE_TO_LINE_TO:
          d =
          /** @type {number} */
          instruction[1];
          dd =
          /** @type {number} */
          instruction[2];
          x = pixelCoordinates[d];
          y = pixelCoordinates[d + 1];
          roundX = x + 0.5 | 0;
          roundY = y + 0.5 | 0;

          if (roundX !== prevX || roundY !== prevY) {
            context.moveTo(x, y);
            prevX = roundX;
            prevY = roundY;
          }

          for (d += 2; d < dd; d += 2) {
            x = pixelCoordinates[d];
            y = pixelCoordinates[d + 1];
            roundX = x + 0.5 | 0;
            roundY = y + 0.5 | 0;

            if (d == dd - 2 || roundX !== prevX || roundY !== prevY) {
              context.lineTo(x, y);
              prevX = roundX;
              prevY = roundY;
            }
          }

          ++i;
          break;

        case _Instruction["default"].SET_FILL_STYLE:
          lastFillInstruction = instruction;
          this.alignFill_ = instruction[2];

          if (pendingFill) {
            this.fill_(context);
            pendingFill = 0;

            if (pendingStroke) {
              context.stroke();
              pendingStroke = 0;
            }
          }

          context.fillStyle =
          /** @type {import("../../colorlike.js").ColorLike} */
          instruction[1];
          ++i;
          break;

        case _Instruction["default"].SET_STROKE_STYLE:
          lastStrokeInstruction = instruction;

          if (pendingStroke) {
            context.stroke();
            pendingStroke = 0;
          }

          this.setStrokeStyle_(context,
          /** @type {Array<*>} */
          instruction);
          ++i;
          break;

        case _Instruction["default"].STROKE:
          if (batchSize) {
            pendingStroke++;
          } else {
            context.stroke();
          }

          ++i;
          break;

        default:
          ++i; // consume the instruction anyway, to avoid an infinite loop

          break;
      }
    }

    if (pendingFill) {
      this.fill_(context);
    }

    if (pendingStroke) {
      context.stroke();
    }

    return undefined;
  };
  /**
   * @param {CanvasRenderingContext2D} context Context.
   * @param {number} contextScale Scale of the context.
   * @param {import("../../transform.js").Transform} transform Transform.
   * @param {number} viewRotation View rotation.
   * @param {boolean} snapToPixel Snap point symbols and text to integer pixels.
   * @param {import("rbush").default} [opt_declutterTree] Declutter tree.
   */


  Executor.prototype.execute = function (context, contextScale, transform, viewRotation, snapToPixel, opt_declutterTree) {
    this.viewRotation_ = viewRotation;
    this.execute_(context, contextScale, transform, this.instructions, snapToPixel, undefined, undefined, opt_declutterTree);
  };
  /**
   * @param {CanvasRenderingContext2D} context Context.
   * @param {import("../../transform.js").Transform} transform Transform.
   * @param {number} viewRotation View rotation.
   * @param {FeatureCallback<T>} [opt_featureCallback] Feature callback.
   * @param {import("../../extent.js").Extent} [opt_hitExtent] Only check
   *     features that intersect this extent.
   * @return {T|undefined} Callback result.
   * @template T
   */


  Executor.prototype.executeHitDetection = function (context, transform, viewRotation, opt_featureCallback, opt_hitExtent) {
    this.viewRotation_ = viewRotation;
    return this.execute_(context, 1, transform, this.hitDetectionInstructions, true, opt_featureCallback, opt_hitExtent);
  };

  return Executor;
}();

var _default = Executor;
exports["default"] = _default;
//# sourceMappingURL=data:application/json;charset=utf-8;base64,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